import * as React from "react";
import find from "lodash/find";
import styled, { css } from "styled-components";
import { Button, ButtonToolbar, Col, Nav, NavItem, Row } from "components/bootstrap";
import Icon from "./Icon";
const SubnavigationCol = styled(Col)(
  ({ theme }) => css`
    border-right: ${theme.colors.gray[80]} solid 1px;
  `
);
const HorizontalCol = styled(Col)`
  margin-bottom: 15px;
`;
const StyledNav = styled(Nav)(
  ({ $style, theme }) => css`
    ${$style === "stepper" ? `
  &.nav {
   counter-reset: line-number;
    > li {
      counter-increment: line-number;
      > a {
        position: relative;
        display: flex;
        padding: 10px 0px;
        justify-content: center;
        align-items: center;
        &:hover,
        &:focus {
          background-color: initial;
        }
        > div {
          flex-shrink: 0;
        }
        
        &::before {
          display: flex;
          justify-content: center;
          align-items: center;
          flex-grow: 0; 
          flex-shrink: 0;
          background-color: ${theme.colors.global.contentBackground};
          border-color: ${theme.colors.variant.lighter.default};
          margin-right: 10px;
          content: counter(line-number);
          width: 35px;
          height: 35px;
          border-radius: 50%;
          border: 2px solid;
          z-index: 2;
        }
        &::after {
          display: flex;
          justify-content: center;
          align-items: center;
          background-color: ${theme.colors.global.contentBackground};
          border-color: ${theme.colors.variant.lighter.default};
          margin-right: 10px;
          content: ' ';
          border: 1px solid ${theme.colors.input.border};
          width: 100%;
          flex-shrink: 1;
          flex-grow: 0;
          align-self: center;
          margin: 0 16px;
        } 
        &:hover::after {
          background-color: ${theme.colors.variant.lightest.default};
        }
      }
      &:last-child > a {
       justify-content: flex-start;
       &::after {
        display:none;
       } 
      }
      &.disabled > a {
        color: ${theme.colors.variant.light.default};

        &:hover,
        &:focus {
          color: ${theme.colors.variant.light.default};
        }
      }
    }

    .open > a {
      &,
      &:hover,
      &:focus {
        background-color: inital;
        border-color: ${theme.colors.variant.primary};
      }
    }
    &.nav-justified {
     > li {
      > a {
        text-align: left;
      }
     }
    }
    &.nav-pills {
      > li {
        > a {
          color: initial;

          &:hover  {
            color: ${theme.colors.global.link};
          }
        }

        &.active > a {
          &,
          &:hover,
          &:focus {
            color: ${theme.colors.global.link};
            background-color: initial; 
          }
        }
      }
    } 
  }
  ` : `&.nav {
    > li {
      border: 1px solid ${theme.colors.variant.lighter.default};
      border-left: 0;

      &:first-child {
        border-left: 1px solid ${theme.colors.variant.lighter.default};
        border-radius: 4px 0 0 4px;

        > a {
          border-radius: 4px 0 0 4px;
        }
      }

      &:last-child {
        border-radius: 0 4px 4px 0;

        > a {
          border-radius: 0 4px 4px 0;
        }
      }

      &:not(:last-child) > a {
        &::after {
          transition: background-color 150ms ease-in-out;
          background-color: ${theme.colors.global.contentBackground};
          border-color: ${theme.colors.variant.lighter.default};
          border-style: solid;
          border-width: 0 1px 1px 0;
          content: '';
          display: block;
          height: 15px;
          position: absolute;
          right: -1px;
          top: 50%;
          transform: translateY(-50%) translateX(50%) rotate(-45deg);
          width: 15px;
          z-index: 2;
        }

        &:hover::after {
          background-color: ${theme.colors.variant.lightest.default};
        }
      }

      &.active a {
        &,
        &:hover,
        &::after,
        &:hover::after {
          background-color: ${theme.colors.global.link};
        }
      }
      
      &.disabled > a {
        color: ${theme.colors.text.disabled};
      }

      > a {
        border-radius: 0;
      }
    }
  }

  @media (max-width: ${theme.breakpoints.max.md}) {
    &.nav {
      > li {
        border-right: 0;
        border-left: 0;

        &:last-child a,
        &:first-child a {
          border-radius: 0;
        }

        &:not(:last-child) {
          border-bottom: 0;
        }

        &:not(:last-child) > a {
          &::after {
            bottom: 0;
            left: 50%;
            top: auto;
            width: 10px;
            height: 10px;
            transform: translateY(50%) translateX(-50%) rotate(45deg);
          }
        }
      }

      &.nav-justified > li > a {
        margin-bottom: 0;
      }
    }
  }`}
  `
);
const HorizontalButtonToolbar = styled(ButtonToolbar)`
  padding: 7px;
`;
const isValidActiveStep = (activeStep, steps) => {
  if (activeStep === void 0 || activeStep === null) {
    return false;
  }
  return find(steps, { key: activeStep });
};
const warnOnInvalidActiveStep = (activeStep, steps) => {
  if (activeStep === void 0 || activeStep === null) {
    return;
  }
  if (!isValidActiveStep(activeStep, steps)) {
    console.warn(`activeStep ${activeStep} is not a key in any element of the 'steps' prop!`);
  }
};
class Wizard extends React.Component {
  constructor(props) {
    super(props);
    this._getSelectedStep = () => {
      const { activeStep, steps } = this.props;
      const { selectedStep } = this.state;
      return isValidActiveStep(activeStep, steps) ? activeStep : selectedStep;
    };
    this._wizardChanged = (eventKey) => {
      const { activeStep, onStepChange } = this.props;
      onStepChange(eventKey);
      if (!activeStep) {
        this.setState({ selectedStep: eventKey });
      }
    };
    this._disableButton = (direction) => {
      const { steps } = this.props;
      const selectedStep = this._getSelectedStep();
      const len = steps.length;
      const disabledPosition = direction === "next" ? len - 1 : 0;
      const currentPosition = steps.findIndex((step) => step.key === this._getSelectedStep());
      const otherPosition = direction === "next" ? currentPosition + 1 : currentPosition - 1;
      const otherStep = steps[otherPosition];
      return steps[disabledPosition].key === selectedStep || (otherStep == null ? void 0 : otherStep.disabled);
    };
    this._onNext = () => {
      const { steps } = this.props;
      this._wizardChanged(steps[this._getSelectedIndex() + 1].key);
    };
    this._onPrevious = () => {
      const { steps } = this.props;
      this._wizardChanged(steps[this._getSelectedIndex() - 1].key);
    };
    this._getSelectedIndex = () => {
      const { steps } = this.props;
      const selectedStep = this._getSelectedStep();
      return steps.map((step) => step.key).indexOf(selectedStep);
    };
    this._renderVerticalStepNav = () => {
      const { justified, steps, hidePreviousNextButtons, style } = this.props;
      const selectedStep = this._getSelectedStep();
      return /* @__PURE__ */ React.createElement(SubnavigationCol, { md: 2 }, /* @__PURE__ */ React.createElement(
        StyledNav,
        {
          stacked: true,
          bsStyle: "pills",
          $style: style,
          activeKey: selectedStep,
          onSelect: this._wizardChanged,
          justified
        },
        steps.map((navItem) => /* @__PURE__ */ React.createElement(NavItem, { key: navItem.key, eventKey: navItem.key, disabled: navItem.disabled }, navItem.title))
      ), !hidePreviousNextButtons && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("br", null), /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { xs: 6 }, /* @__PURE__ */ React.createElement(
        Button,
        {
          onClick: this._onPrevious,
          bsSize: "small",
          bsStyle: "info",
          disabled: this._disableButton("previous")
        },
        "Previous"
      )), /* @__PURE__ */ React.createElement(Col, { className: "text-right", xs: 6 }, /* @__PURE__ */ React.createElement(Button, { onClick: this._onNext, bsSize: "small", bsStyle: "info", disabled: this._disableButton("next") }, "Next")))));
    };
    this._renderHorizontalStepNav = () => {
      const selectedStep = this._getSelectedStep();
      const { justified, steps, hidePreviousNextButtons, style } = this.props;
      return /* @__PURE__ */ React.createElement(HorizontalCol, { sm: 12 }, !hidePreviousNextButtons && /* @__PURE__ */ React.createElement("div", { className: "pull-right" }, /* @__PURE__ */ React.createElement(HorizontalButtonToolbar, null, /* @__PURE__ */ React.createElement(
        Button,
        {
          onClick: this._onPrevious,
          "aria-label": "Previous",
          bsSize: "xsmall",
          bsStyle: "info",
          disabled: this._disableButton("previous")
        },
        /* @__PURE__ */ React.createElement(Icon, { name: "arrow_left" })
      ), /* @__PURE__ */ React.createElement(
        Button,
        {
          onClick: this._onNext,
          "aria-label": "Next",
          bsSize: "xsmall",
          bsStyle: "info",
          disabled: this._disableButton("next")
        },
        /* @__PURE__ */ React.createElement(Icon, { name: "arrow_right" })
      ))), /* @__PURE__ */ React.createElement(
        StyledNav,
        {
          bsStyle: "pills",
          activeKey: selectedStep,
          $style: style,
          onSelect: this._wizardChanged,
          justified
        },
        steps.map((navItem) => /* @__PURE__ */ React.createElement(NavItem, { key: navItem.key, eventKey: navItem.key, disabled: navItem.disabled }, navItem.title))
      ));
    };
    warnOnInvalidActiveStep(props.activeStep, props.steps);
    this.state = {
      selectedStep: props.steps[0].key
    };
  }
  componentDidUpdate() {
    const { activeStep, steps } = this.props;
    warnOnInvalidActiveStep(activeStep, steps);
  }
  render() {
    const { steps, horizontal, containerClassName, children } = this.props;
    let leftComponentCols;
    if (children) {
      leftComponentCols = 7;
    } else {
      leftComponentCols = horizontal ? 12 : 10;
    }
    const rightComponentCols = horizontal ? 5 : 3;
    return /* @__PURE__ */ React.createElement(Row, { className: containerClassName }, horizontal ? this._renderHorizontalStepNav() : this._renderVerticalStepNav(), /* @__PURE__ */ React.createElement(Col, { md: leftComponentCols }, steps[this._getSelectedIndex()].component), children && /* @__PURE__ */ React.createElement(Col, { md: rightComponentCols }, children));
  }
}
Wizard.defaultProps = {
  children: void 0,
  activeStep: void 0,
  horizontal: false,
  justified: false,
  containerClassName: "content",
  hidePreviousNextButtons: false,
  style: void 0
};
export default Wizard;
